/**
 * Utilities for detecting and organizing book series
 */

export interface SeriesInfo {
  seriesName: string;
  position?: number;
  total?: number;
}

export interface SeriesGroup {
  seriesName: string;
  books: any[];
  isComplete: boolean;
  totalBooks?: number;
}

/**
 * Extract series information from book title
 * Handles patterns like:
 * - "Title (Series #1)"
 * - "Title: Series Book 1"
 * - "Series, Book 1: Title"
 * - "Title (Series, Part 2)"
 */
export function detectSeriesFromTitle(title: string): SeriesInfo | null {
  if (!title) return null;

  // Pattern: (Series Name #1) or (Series Name, Book 1)
  const pattern1 = /\(([^)]+?)(?:,?\s*(?:#|Book|Part|Volume|Vol\.?)\s*(\d+(?:\.\d+)?))\)/i;
  const match1 = title.match(pattern1);
  if (match1) {
    return {
      seriesName: match1[1].trim(),
      position: parseFloat(match1[2])
    };
  }

  // Pattern: Series Name #1: Title or Series Name Book 1: Title
  const pattern2 = /^([^:#]+?)(?:#|Book|Part|Volume|Vol\.?)\s*(\d+(?:\.\d+)?)\s*[:-]/i;
  const match2 = title.match(pattern2);
  if (match2) {
    return {
      seriesName: match2[1].trim(),
      position: parseFloat(match2[2])
    };
  }

  // Pattern: Title - Book 1 or Title, Part 2
  const pattern3 = /[,\-]\s*(?:Book|Part|Volume|Vol\.?)\s*(\d+(?:\.\d+)?)$/i;
  const match3 = title.match(pattern3);
  if (match3) {
    // Extract potential series name before the book number
    const baseTitleMatch = title.match(/^(.+?)\s*[,\-]\s*(?:Book|Part|Volume|Vol\.?)/i);
    if (baseTitleMatch) {
      return {
        seriesName: baseTitleMatch[1].trim(),
        position: parseFloat(match3[1])
      };
    }
  }

  return null;
}

/**
 * Group books by series
 */
export function groupBooksBySeries(books: any[]): {
  seriesGroups: SeriesGroup[];
  standaloneBooks: any[];
} {
  const seriesMap = new Map<string, any[]>();
  const standaloneBooks: any[] = [];

  books.forEach(book => {
    // Check if book has explicit series info
    if (book.series_name) {
      const key = book.series_name.toLowerCase();
      if (!seriesMap.has(key)) {
        seriesMap.set(key, []);
      }
      seriesMap.get(key)!.push(book);
    } else {
      // Try to detect series from title
      const seriesInfo = detectSeriesFromTitle(book.title);
      if (seriesInfo) {
        const key = seriesInfo.seriesName.toLowerCase();
        if (!seriesMap.has(key)) {
          seriesMap.set(key, []);
        }
        // Attach detected series info to book
        seriesMap.get(key)!.push({
          ...book,
          detectedSeriesName: seriesInfo.seriesName,
          detectedPosition: seriesInfo.position
        });
      } else {
        standaloneBooks.push(book);
      }
    }
  });

  // Convert map to sorted series groups
  const seriesGroups: SeriesGroup[] = Array.from(seriesMap.entries())
    .filter(([_, books]) => books.length > 1) // Only include series with multiple books
    .map(([key, books]) => {
      // Sort books by position
      const sortedBooks = books.sort((a, b) => {
        const posA = a.series_position ?? a.detectedPosition ?? 0;
        const posB = b.series_position ?? b.detectedPosition ?? 0;
        return posA - posB;
      });

      const firstBook = sortedBooks[0];
      const seriesName = firstBook.series_name || firstBook.detectedSeriesName;
      const totalBooks = firstBook.series_total;
      
      return {
        seriesName,
        books: sortedBooks,
        isComplete: totalBooks ? sortedBooks.length === totalBooks : false,
        totalBooks
      };
    })
    .sort((a, b) => {
      // Sort series by earliest published year
      const yearA = Math.min(...a.books.map(b => b.published_year || 9999));
      const yearB = Math.min(...b.books.map(b => b.published_year || 9999));
      return yearA - yearB;
    });

  // Add books from series with only 1 book to standaloneBooks
  seriesMap.forEach((books, key) => {
    if (books.length === 1) {
      standaloneBooks.push(books[0]);
    }
  });

  return { seriesGroups, standaloneBooks };
}

/**
 * Get clean title without series information
 */
export function getCleanTitle(title: string, seriesName?: string): string {
  if (!title) return '';
  
  let cleanTitle = title;
  
  // Remove (Series Name #1) pattern
  cleanTitle = cleanTitle.replace(/\([^)]+?(?:#|Book|Part|Volume|Vol\.?)\s*\d+(?:\.\d+)?\)/gi, '');
  
  // Remove Series Name #1: pattern
  if (seriesName) {
    const pattern = new RegExp(`^${seriesName}\\s*(?:#|Book|Part|Volume|Vol\\.?)\\s*\\d+(?:\\.\\d+)?\\s*[:-]\\s*`, 'i');
    cleanTitle = cleanTitle.replace(pattern, '');
  }
  
  // Remove trailing book numbers
  cleanTitle = cleanTitle.replace(/[,\-]\s*(?:Book|Part|Volume|Vol\.?)\s*\d+(?:\.\d+)?$/i, '');
  
  return cleanTitle.trim();
}
